// Tenant configuration utility
export interface TenantConfig {
  id: string;
  name: string;
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  tournamentTitle?: string;
  tournamentDescription?: string;
  features?: Record<string, boolean>;
}

// Get tenant from subdomain or localStorage
export const getTenantId = (): string => {
  const hostname = window.location.hostname;

  // If we're on localhost, check localStorage for tenant
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return localStorage.getItem('tenant') || 'default';
  }

  // Extract subdomain from hostname
  const parts = hostname.split('.');
  if (parts.length > 2) {
    return parts[0]; // First part is the subdomain
  }

  return 'default';
};

// Set tenant for localhost development
export const setTenant = (tenantId: string): void => {
  localStorage.setItem('tenant', tenantId);
  // Update the API client header dynamically
  import('../api/client').then(({ updateTenantHeader }) => {
    updateTenantHeader(tenantId);
  });
};

// Get tenant configuration
export const getTenantConfig = async (): Promise<TenantConfig | null> => {
  // First try to get from injected script (production)
  const tenantScript = document.getElementById('tenant');
  if (tenantScript && tenantScript.textContent) {
    try {
      return JSON.parse(tenantScript.textContent);
    } catch (error) {
      console.error('Error parsing tenant config from script:', error);
    }
  }

  // Fallback to API call (development)
  const tenantId = getTenantId();

  try {
    const response = await fetch(`/api/tenants/${tenantId}`);
    if (!response.ok) {
      console.error(`Tenant ${tenantId} not found`);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching tenant config:', error);
    return null;
  }
};

// Apply tenant configuration to the app
export const applyTenantConfig = (config: TenantConfig): void => {
  // Update document title
  if (config.tournamentTitle) {
    document.title = config.tournamentTitle;
  }

  // Update CSS custom properties for theming
  if (config.primaryColor) {
    document.documentElement.style.setProperty('--primary-color', config.primaryColor);
  }

  if (config.secondaryColor) {
    document.documentElement.style.setProperty('--secondary-color', config.secondaryColor);
  }

  // Store config in window for global access
  (window as any).TENANT_CONFIG = config;
};
